/**
 * Procedural Web Audio Synthesizer for OUTAURA V2.
 * Generates all SFX mathematically with zero network requests or audio file dependencies.
 */
export class SoundEngine {
  private ctx: AudioContext | null = null;
  private isMuted: boolean = false;

  constructor() {
    if (typeof localStorage !== 'undefined') {
      this.isMuted = localStorage.getItem('outaura_muted') === 'true';
    }
  }

  private initCtx(): AudioContext | null {
    if (!this.ctx && typeof window !== 'undefined') {
      const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (AudioCtx) {
        this.ctx = new AudioCtx();
      }
    }
    if (this.ctx && this.ctx.state === 'suspended') {
      this.ctx.resume().catch(() => {});
    }
    return this.ctx;
  }

  public unlock(): void {
    this.initCtx();
  }

  public toggleMute(): boolean {
    this.isMuted = !this.isMuted;
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('outaura_muted', String(this.isMuted));
    }
    return this.isMuted;
  }

  public getMuted(): boolean {
    return this.isMuted;
  }

  /**
   * PERFECT Hit: Dual shimmer chime with musical pentatonic pitch scaling on combo
   */
  public playPerfect(combo: number = 0): void {
    if (this.isMuted) return;
    const ctx = this.initCtx();
    if (!ctx) return;

    // Musical pentatonic scale semitone offsets: Root, M2, M3, P5, M6, Octave, etc.
    const scale = [0, 2, 4, 7, 9, 12, 14, 16, 19, 21, 24];
    const semitones = scale[Math.min(combo, scale.length - 1)];
    const pitchMultiplier = Math.pow(2, semitones / 12);

    const now = ctx.currentTime;
    const osc1 = ctx.createOscillator();
    const osc2 = ctx.createOscillator();
    const gain = ctx.createGain();

    const baseE = 659.25 * pitchMultiplier; // E5 scaled
    const baseB = 987.77 * pitchMultiplier; // B5 scaled

    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(baseE, now);
    osc1.frequency.exponentialRampToValueAtTime(baseE * 2, now + 0.12);

    osc2.type = 'triangle';
    osc2.frequency.setValueAtTime(baseB, now);
    osc2.frequency.exponentialRampToValueAtTime(baseB * 2, now + 0.15);

    // Dynamic volume scaling (louder and crisper as combo builds)
    const volume = Math.min(0.35, 0.22 + Math.min(combo, 15) * 0.012);
    gain.gain.setValueAtTime(volume, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.35);

    osc1.connect(gain);
    osc2.connect(gain);
    gain.connect(ctx.destination);

    osc1.start(now);
    osc2.start(now);
    osc1.stop(now + 0.35);
    osc2.stop(now + 0.35);
  }

  /**
   * GREAT Hit: Crisp clean pop
   */
  public playGreat(): void {
    if (this.isMuted) return;
    const ctx = this.initCtx();
    if (!ctx) return;

    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(493.88, now); // B4
    osc.frequency.exponentialRampToValueAtTime(987.77, now + 0.09); // B5

    gain.gain.setValueAtTime(0.22, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.2);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(now);
    osc.stop(now + 0.2);
  }

  /**
   * GOOD Hit: Muted percussion click / blip
   */
  public playGood(): void {
    if (this.isMuted) return;
    const ctx = this.initCtx();
    if (!ctx) return;

    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'triangle';
    osc.frequency.setValueAtTime(329.63, now); // E4
    osc.frequency.exponentialRampToValueAtTime(220.0, now + 0.07); // A3

    gain.gain.setValueAtTime(0.16, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.09);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(now);
    osc.stop(now + 0.09);
  }

  /**
   * MISS: Low distortion glitch buzz
   */
  public playMiss(): void {
    if (this.isMuted) return;
    const ctx = this.initCtx();
    if (!ctx) return;

    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(160, now);
    osc.frequency.linearRampToValueAtTime(65, now + 0.22);

    gain.gain.setValueAtTime(0.28, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(now);
    osc.stop(now + 0.25);
  }

  /**
   * COMBO MILESTONE: Satisfying audio fanfare at 5x, 10x, 15x, 20x
   */
  public playComboMilestone(milestone: number): void {
    if (this.isMuted) return;
    const ctx = this.initCtx();
    if (!ctx) return;

    const now = ctx.currentTime;

    // Sub-bass impact
    const sub = ctx.createOscillator();
    const subGain = ctx.createGain();
    sub.type = 'sine';
    sub.frequency.setValueAtTime(90, now);
    sub.frequency.exponentialRampToValueAtTime(40, now + 0.35);

    subGain.gain.setValueAtTime(0.35, now);
    subGain.gain.exponentialRampToValueAtTime(0.001, now + 0.45);
    sub.connect(subGain);
    subGain.connect(ctx.destination);
    sub.start(now);
    sub.stop(now + 0.45);

    // Multi-note arpeggio based on milestone tier
    let chordNotes = [523.25, 659.25, 783.99]; // 5x (C5, E5, G5)
    if (milestone >= 20) {
      chordNotes = [523.25, 659.25, 783.99, 1046.5, 1318.5]; // 20x (Full Mythic)
    } else if (milestone >= 15) {
      chordNotes = [523.25, 659.25, 783.99, 1046.5]; // 15x
    } else if (milestone >= 10) {
      chordNotes = [440.0, 554.37, 659.25, 880.0]; // 10x
    }

    chordNotes.forEach((freq, idx) => {
      const start = now + idx * 0.05;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'triangle';
      osc.frequency.setValueAtTime(freq, start);
      osc.frequency.exponentialRampToValueAtTime(freq * 1.5, start + 0.2);

      gain.gain.setValueAtTime(0.18, start);
      gain.gain.exponentialRampToValueAtTime(0.001, start + 0.28);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(start);
      osc.stop(start + 0.28);
    });
  }

  /**
   * COUNTDOWN TICK: Dramatic warning for final 5, 4, 3, 2, 1 seconds
   */
  public playCountdown(secondsLeft: number): void {
    if (this.isMuted) return;
    const ctx = this.initCtx();
    if (!ctx) return;

    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    // Escalating pitch as time runs out (secondsLeft from 5 down to 1)
    const freq = secondsLeft <= 1 ? 1046.5 : (600 + (5 - secondsLeft) * 90);
    osc.type = secondsLeft <= 1 ? 'sawtooth' : 'sine';
    osc.frequency.setValueAtTime(freq, now);

    gain.gain.setValueAtTime(secondsLeft <= 1 ? 0.3 : 0.2, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + (secondsLeft <= 1 ? 0.2 : 0.08));

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(now);
    osc.stop(now + 0.22);
  }

  /**
   * GAME OVER VICTORY FANFARE
   */
  public playVictory(): void {
    if (this.isMuted) return;
    const ctx = this.initCtx();
    if (!ctx) return;

    const now = ctx.currentTime;
    const notes = [523.25, 659.25, 783.99, 1046.5]; // C5, E5, G5, C6
    notes.forEach((freq, idx) => {
      const start = now + idx * 0.08;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'triangle';
      osc.frequency.setValueAtTime(freq, start);

      gain.gain.setValueAtTime(0.24, start);
      gain.gain.exponentialRampToValueAtTime(0.001, start + 0.28);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(start);
      osc.stop(start + 0.28);
    });
  }

  /**
   * GAME OVER DEFEAT SOUND
   */
  public playDefeat(): void {
    if (this.isMuted) return;
    const ctx = this.initCtx();
    if (!ctx) return;

    const now = ctx.currentTime;
    const notes = [440, 392, 349.23, 293.66]; // A4, G4, F4, D4
    notes.forEach((freq, idx) => {
      const start = now + idx * 0.12;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(freq, start);

      gain.gain.setValueAtTime(0.18, start);
      gain.gain.exponentialRampToValueAtTime(0.001, start + 0.22);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(start);
      osc.stop(start + 0.22);
    });
  }
}

export const sound = new SoundEngine();
